import store from '../../common-store'
import username from '../../../lib/username'
import { SyncMap } from 'twilio-sync'
import { SeatingOrderParticipant } from '../../reducers/docs/syncSeatingOrder/types'
import { State } from '../../reducers/reducerType'
import isEmpty from 'lodash/isEmpty'
import { debug } from '@bt-react/libs/Logger'

/**
 * Amend the update object as the participant enters
 * @param currentState
 * @param identity
 */
export function getNewEntryState (currentState: SeatingOrderParticipant, identity: string): SeatingOrderParticipant {

  if (username.isDialIn(identity)) {

    if (currentState.presenterStatus === 'disconnected') currentState['seatedDatetime'] = new Date(Date.now()).toISOString()
    currentState['dialInStatus'] = 'connected'

  }

  if (username.isPresenter(identity)) {

    if (currentState.dialInStatus === 'disconnected') currentState['seatedDatetime'] = new Date(Date.now()).toISOString()
    currentState['presenterStatus'] = 'connected'

  }

  return currentState

}

export async function fetchMapState (map: SyncMap, identity: string): Promise<SeatingOrderParticipant> {

  // Set defaults
  let update: SeatingOrderParticipant = {
    dialInStatus: 'disconnected',
    presenterStatus: 'disconnected',
    seatedDatetime: null
  }

  // Merge current state if exists
  try {

    const entry = await map.get(identity)
    update = {
      ...update,
      ...entry.data
    }

  }
  catch (e) {

    debug(e.message)

  }

  return update

}

export function shouldIDispatchUpdates (store, identity?: string): boolean {

  const { user: { BTID = '' } } = store.getState() as State
  if (identity && username.isPresenter(identity)) return BTID === identity

  const {
    activeBridge,
    bridgeName
  } = store.getState().config

  if (!activeBridge) return true
  return activeBridge === bridgeName

}

/**
 * addSeatToMap is called either locally by a PRESENTER or on entry of a remote participant.
 * If it's a remote participant that isn't a presenter, it should be enacted by the main bridge if the bridge is up or the local participant if there's no bridge
 * @param identity
 */
export async function addSeatToMap (identity: string) {

  debug(`Sync Seat - Checking participant's role`)

  if (!username.isPresenter(identity) && !username.isDialIn(identity)) return

  debug('Sync Seat - Verifying dispatch')

  if (!shouldIDispatchUpdates(store, identity)) return

  const { seatingOrderMap } = store.getState().docs

  debug('Sync Seat - Verifying dependencies')

  if (!seatingOrderMap) return

  const BTID = username.getPureUsername(identity)

  debug('Sync Seat - Fetching state')

  let update = await fetchMapState(seatingOrderMap, BTID)
  update = getNewEntryState(update, identity)

  debug('Sync Seat - Applying state')

  if (!isEmpty(update)) {

    try {

      await seatingOrderMap.update(BTID, update)
      debug('Sync Seat - Complete')

    }
    catch (e) {

      debug('Sync Seat - Error', e.message)

    }

  }

}

export async function removeSeatFromMap (identity: string) {

  debug('Sync Seat Removal - Verifying dispatch')

  if (!shouldIDispatchUpdates(store)) return

  const { seatingOrderMap } = store.getState().docs

  debug('Sync Seat Removal - Verifying dependencies')

  if (!seatingOrderMap) return

  debug('Sync Seat Removal - Fetching state')

  const BTID = username.getPureUsername(identity)
  const update = await fetchMapState(seatingOrderMap, BTID)

  // Set new values based on entry
  if (username.isDialIn(identity)) update['dialInStatus'] = 'disconnected'
  if (username.isPresenter(identity)) update['presenterStatus'] = 'disconnected'

  debug('Sync Seat Removal - Applying state')

  if (!isEmpty(update)) {

    try {

      if (update.dialInStatus === 'disconnected' && update.presenterStatus === 'disconnected') {

        await seatingOrderMap.remove(BTID)
        debug('Sync Seat Removal - Removed entry')

      }
      else {

        await seatingOrderMap.update(BTID, update)
        debug('Sync Seat Removal - Updated entry status')

      }

    }
    catch (e) {

      debug('Sync Seat Removal - Error', e.message)

    }

  }

}
